import React, { useRef, useState } from "react";
import { api, ApiError } from "../../../../api/apiClient";
import Toggle from "../../../components-ui/Toggle";
import { notify } from "../../../components-ui/toast";
import AppDatePicker from "../../../components-ui/AppDatePicker";
import AppTimePicker from "../../../components-ui/AppTimePicker";
import dayjs from "dayjs";

import "../../../../css/forms.css";
import "../../../../css/buttons.css";
import "../../../../css/cards.css";

/* =========================
 * Types
 * ========================= */

type Frequency = "Weekly" | "Fortnightly" | "Monthly";

interface CreateServiceRecurrenceRequest {
  frequency: Frequency;
  intervalValue: number;
  daysOfWeek: string[];
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  timezone: string;
}

interface CreateServiceInstanceRequest {
  startDateTime: string;
  endDateTime: string;
  capacity?: number;
  notes?: string;
  recurrence?: CreateServiceRecurrenceRequest;
}

/* =========================
 * Props
 * ========================= */

interface CreateServiceInstanceFormProps {
  serviceId: number;
  serviceName: string;
  onCreated: () => void;
}

/* =========================
 * Component
 * ========================= */

export default function CreateServiceInstanceForm({
  serviceId,
  serviceName,
  onCreated,
}: CreateServiceInstanceFormProps) {
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [capacity, setCapacity] = useState<number | "">("");
  const [notes, setNotes] = useState("");

  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>("Weekly");
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const submittingRef = useRef(false);

  function toggleDay(day: string) {
    setDaysOfWeek((d) =>
      d.includes(day) ? d.filter((x) => x !== day) : [...d, day],
    );
  }

  async function handleSubmit() {
    if (submittingRef.current) return;

    setError(null);

    if (!date || !startTime || !endTime) {
      setError({
        message: "Date, start time and end time are required.",
      } as ApiError);
      return;
    }

    if (isRecurring) {
      if (!recurrenceEndDate) {
        setError({
          message: "End date is required for recurring services.",
        } as ApiError);
        return;
      }

      if (recurrenceEndDate < date) {
        setError({
          message: "End date must be on or after the start date.",
        } as ApiError);
        return;
      }

      if (daysOfWeek.length === 0) {
        setError({
          message: "Please select at least one day of the week.",
        } as ApiError);
        return;
      }
    }

    const startDateTime = `${date}T${startTime}:00Z`;
    const endDateTime = `${date}T${endTime}:00Z`;

    const payload: CreateServiceInstanceRequest = {
      startDateTime,
      endDateTime,
      capacity: capacity === "" ? undefined : capacity,
      notes: notes || undefined,
    };

    if (isRecurring) {
      payload.recurrence = {
        frequency,
        intervalValue: 1,
        daysOfWeek,
        startTime,
        endTime,
        startDate: date,
        endDate: recurrenceEndDate,
        timezone: "Europe/London",
      };
    }

    try {
      submittingRef.current = true;
      setSubmitting(true);

      await api(
        `/api/serviceinstances/create-service-instances/${serviceId}/instances`,
        {
          method: "POST",
          body: payload,
        },
      );

      notify.success("Service instances created successfully");
      onCreated();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {error && <div className="error">{error.message}</div>}

      <div className="form-group">
        <label className="form-label">Service</label>
        <input
          className="form-input"
          type="text"
          value={serviceName}
          readOnly
        />
      </div>

      {/* Date */}
      <div className="form-group">
        <label>Date</label>
        <AppDatePicker
          value={date}
          maxDate={dayjs().add(2, "year")}
          onChange={(value) => setDate(value ?? "")}
        />
      </div>

      {/* Times */}
      <div className="form-row">
        <div className="form-group">
          <label>Start time</label>
          <AppTimePicker
            value={startTime}
            onChange={(value) => setStartTime(value ?? "")}
          />
        </div>

        <div className="form-group">
          <label>End time</label>
          <AppTimePicker
            value={endTime}
            onChange={(value) => setEndTime(value ?? "")}
          />
        </div>
      </div>

      {/* Capacity */}
      <div className="form-group">
        <label>Capacity (optional)</label>
        <input
          className="form-input"
          type="number"
          value={capacity}
          onChange={(e) =>
            setCapacity(e.target.value === "" ? "" : Number(e.target.value))
          }
        />
      </div>

      {/* Notes */}
      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Recurrence toggle */}
      <Toggle
        name="recurringSession"
        label="This service repeats"
        checked={isRecurring}
        onChange={setIsRecurring}
      />

      {isRecurring && (
        <>
          <div className="form-group">
            <label>Frequency</label>
            <select
              className="form-input"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency)}
            >
              <option value="Weekly">Weekly</option>
              <option value="Fortnightly">Fortnightly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>

          <div className="form-group">
            <div className="card-form-section-title">Days of week</div>

            <div className="inline-form">
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ].map((day) => (
                <label
                  key={day}
                  style={{ display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    checked={daysOfWeek.includes(day)}
                    onChange={() => toggleDay(day)}
                    style={{ width: "auto" }}
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>End date</label>
            <AppDatePicker
              value={recurrenceEndDate}
              minDate={dayjs(date)}
              onChange={(value) => setRecurrenceEndDate(value ?? "")}
            />
          </div>
        </>
      )}

      <div className="form-actions">
        <button
          className="form-submit-button"
          type="submit"
          disabled={submitting}
        >
          {submitting ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
